<template>
  <div class="remaining-credits">
    <v-skeleton-loader
      v-if="creditsPending"
      type="text"
      :max-width="170"
    />
    <div v-else-if="credits" class="d-flex align-center" data-locator="credits">
      <app-icon-badge
        :data-locator="dotStatusLocator"
        :color="dotColor"
        :bordered="false"
        dot
        inline
      />

      <div class="d-flex align-center column-gap-1 text--x-small" data-locator="credits-count">
        <div>
          <span data-locator="remaining">{{ remaining }}</span>/<span data-locator="total">{{ total }}</span>
        </div>
        <span>{{ creditsLabelComputed }}</span>
      </div>

      <AppTooltip v-if="renewalDate" data-locator="documents-summary-credits-renewal-tooltip">
        <template #activator="{ props }">
          <app-material-icon name="info" v-bind="props" data-locator="information-icon" />
        </template>
        <i18n-t
          :keypath="`${TRANSLATION_KEY_PREFIX}.remainingCreditsTooltip`"
          scope="global"
          tag="span"
        >
          <template #renewalDate>
            <span class="font-weight-bold">{{ renewalDate }}</span>
          </template>
        </i18n-t>
        <div v-if="remaining === 0">
          {{ $t(`${TRANSLATION_KEY_PREFIX}.remainingCreditsTooltipContact`) }}
        </div>
      </AppTooltip>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import AppTooltip from '@/common/AppTooltip.vue'
import { ISOToDDMMYYYY } from '@/common/utils/dates'
import type { AICredits } from '@/types/ai-assistant'

const THRESHOLD_WARNING = 3
const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantRemainingCredits'

export default defineComponent({
  name: 'AIAssistantRemainingCredits',
  components: { AppTooltip },
  props: {
    credits: {
      type: [Object as () => AICredits, null],
      required: false,
      default: null,
    },
    creditsLabel: {
      type: String,
      default: `${TRANSLATION_KEY_PREFIX}.remainingCreditsLabel`,
    },
    creditsPending: {
      type: Boolean,
    },
    creditsThresholdWarning: {
      type: Number,
      default: THRESHOLD_WARNING,
    },
  },
  data () {
    return {
      THRESHOLD_WARNING,
      TRANSLATION_KEY_PREFIX,
    }
  },
  computed: {
    creditsLabelComputed (): string {
      return this.$t(this.creditsLabel, {}, this.remaining)
    },
    total () {
      return (this.credits as AICredits).total
    },
    remaining () {
      return (this.credits as AICredits).remaining
    },
    dotColor () {
      if (this.remaining <= 1) return '#EF0731'
      if (this.remaining <= this.THRESHOLD_WARNING) return '#F58E33'
      return '#14B88F'
    },
    dotStatusLocator () {
      if (this.remaining === 0) return 'error-status'
      if (this.remaining <= this.THRESHOLD_WARNING) return 'warning-status'
      return 'ok-status'
    },
    renewalDate () {
      return this.credits && (this.credits as AICredits).renewalDate
        ? ISOToDDMMYYYY((this.credits as AICredits).renewalDate as string)
        : null
    },
  },
})
</script>